import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileAudio, Loader2, UploadCloud, Wand2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { ACCEPT, MAX_BYTES, uploadVocal } from "@/lib/enhanceApi";
import { safeApiError } from "@/lib/safeErrorText";

const mb = (n) => `${(n / (1024 * 1024)).toFixed(1)} MB`;
const OK = /\.(wav|mp3|m4a|aac|flac|aiff?|ogg)$/i;

/** Pick or drop a vocal (WAV, MP3, M4A…), name it, optionally strip the music, then enhance. */
export default function UploadCard() {
  const navigate = useNavigate();
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [isolate, setIsolate] = useState(false);
  const [progress, setProgress] = useState(null);
  const [over, setOver] = useState(false);
  const pick = (f) => {
    if (!f) return;
    if (!OK.test(f.name)) { toast.error("Use a WAV, MP3, M4A, AAC, FLAC, AIFF or OGG file."); return; }
    if (f.size > MAX_BYTES) { toast.error("That file is over 300 MB — export a shorter or MP3 version."); return; }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ""));
  };
  const go = async () => {
    setProgress(0);
    try { const item = await uploadVocal(file, { title: title.trim(), isolate }, setProgress); navigate(`/create/enhance/${item.id}`); }
    catch (e) { toast.error("Upload didn't finish", { description: safeApiError(e) }); setProgress(null); }
  };
  const busy = progress !== null;
  return (
    <section data-testid="enhance-upload" className="rounded-3xl border border-white/[0.08] bg-[#0a0d1c]/80 p-5 sm:p-7">
      <button type="button" data-testid="enhance-dropzone" disabled={busy} onClick={() => input.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); pick(e.dataTransfer.files?.[0]); }}
        className={`flex w-full flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-colors ${over ? "border-amber-300 bg-amber-300/10" : "border-white/15 bg-black/30 hover:border-white/30"}`}>
        {file ? <FileAudio className="h-9 w-9 text-amber-300" /> : <UploadCloud className="h-9 w-9 text-white/50" />}
        <span data-testid="enhance-file-name" className="font-display text-[15px] font-semibold">{file ? file.name : "Choose or drop your vocal recording"}</span>
        <span className="text-[12px] text-white/45">{file ? mb(file.size) : "WAV, MP3, M4A, AAC, FLAC, AIFF or OGG · up to 15 minutes"}</span>
      </button>
      <input ref={input} type="file" accept={ACCEPT} className="hidden" data-testid="enhance-file-input" onChange={(e) => pick(e.target.files?.[0])} />
      <label className="mb-1 mt-5 block font-mono text-[10px] uppercase tracking-[0.16em] text-white/50" htmlFor="enhance-title">Song title</label>
      <input id="enhance-title" data-testid="enhance-title" value={title} maxLength={120} disabled={busy} onChange={(e) => setTitle(e.target.value)}
        placeholder="e.g. True Love Comes From Within"
        className="w-full rounded-xl border border-white/10 bg-black/40 px-3.5 py-2.5 text-[14px] text-white placeholder:text-white/30 focus:border-amber-300/50 focus:outline-none" />
      <label className="mt-4 flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-black/30 px-4 py-3">
        <span>
          <span className="block text-[13px] font-medium">Remove the music first</span>
          <span className="block text-[11px] text-white/45">Only if there's a beat or band behind your voice. Adds about a minute and uses AI credits.</span>
        </span>
        <Switch data-testid="enhance-isolate" checked={isolate} disabled={busy} onCheckedChange={setIsolate} />
      </label>
      {busy ? (
        <div className="mt-5" data-testid="enhance-upload-progress">
          <Progress value={Math.round(progress * 100)} className="h-2 bg-white/10" />
          <p className="mt-2 text-[12px] text-white/55">{progress < 1 ? `Uploading… ${Math.round(progress * 100)}%` : "Starting…"}</p>
        </div>
      ) : (
        <button type="button" data-testid="enhance-start" disabled={!file} onClick={go}
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-amber-300 py-3 text-[14px] font-semibold text-black transition-transform hover:scale-[1.01] disabled:opacity-40 disabled:hover:scale-100">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Enhance my vocal
        </button>
      )}
    </section>
  );
}
